// src/components/StringerPortalView.tsx
import React, { useState } from 'react';
import { ArrowLeft, Scissors, Clock, ArrowUpDown } from 'lucide-react';
import { formatPrice, formatDateEU } from '../utils/formatters';

interface StringerPortalViewProps {
    selectedStringerPortal: string;
    stringers: any[];
    jobs: any[];
    customers: any[];
    
    // Functies uit App.tsx
    setSelectedStringerPortal: (val: string | null) => void;
    setActiveTab: (tab: string) => void;
    getCustomerName: (id: string) => string;
    getRacketName: (job: any) => string;
    openCustomerHistory: (customer: any) => void;
    handleBulkPayStringer: (jobs: any[]) => void;
    toggleStringerPaid: (job: any) => void;
    onUpdateStringerRate: (stringerId: string, rate: number) => void;
}

export default function StringerPortalView({
    selectedStringerPortal,
    stringers,
    jobs,
    customers,
    setSelectedStringerPortal,
    setActiveTab,
    getCustomerName,
    getRacketName,
    openCustomerHistory,
    handleBulkPayStringer,
    toggleStringerPaid,
    onUpdateStringerRate
}: StringerPortalViewProps) {

    // --- LOKALE STATE ---
    const [stringerSortConfig, setStringerSortConfig] = useState({ key: 'date', direction: 'descending' });

    // --- LOKALE FUNCTIE ---
    const requestStringerSort = (key: string) => {
        let direction = 'ascending';
        if (stringerSortConfig.key === key && stringerSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setStringerSortConfig({ key, direction });
    };

    // --- DATA FILTERING & BEREKENING ---
    const currentStringer = stringers.find(s => s.name === selectedStringerPortal);
    if (!currentStringer) return <div className="p-4 text-center">Bespanner niet gevonden</div>;

    let sJobs = jobs.filter(j => j.stringer === selectedStringerPortal);

    sJobs.sort((a, b) => {
        let valA, valB;
        if (stringerSortConfig.key === 'date') {
            valA = new Date(a.date).getTime() || 0; valB = new Date(b.date).getTime() || 0;
        } else if (stringerSortConfig.key === 'customer') {
            valA = getCustomerName(a.customerId).toLowerCase(); valB = getCustomerName(b.customerId).toLowerCase();
        } else if (stringerSortConfig.key === 'racket') {
            valA = getRacketName(a).toLowerCase(); valB = getRacketName(b).toLowerCase();
        } else if (stringerSortConfig.key === 'status') {
            valA = a.status.toLowerCase(); valB = b.status.toLowerCase();
        } else {
            valA = (a[stringerSortConfig.key] || '').toString().toLowerCase();
            valB = (b[stringerSortConfig.key] || '').toString().toLowerCase();
        }
        if (valA < valB) return stringerSortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return stringerSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });
    
    const completedJobs = sJobs.filter(j => j.status === 'Klaar');
    const openForPayout = completedJobs.filter(j => !j.stringerPaid);
    const rate = currentStringer.rate || 0;
    const totalOwed = openForPayout.length * rate;

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
             <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button onClick={() => { setSelectedStringerPortal(null); setActiveTab('settings'); }} className="p-2 mr-4 bg-slate-100 dark:bg-slate-800 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition"><ArrowLeft className="w-6 h-6 text-slate-600 dark:text-slate-300" /></button>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Dossier: {selectedStringerPortal}</h2>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center"><div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl mr-4"><Scissors className="w-6 h-6 text-indigo-600 dark:text-indigo-400" /></div><div><p className="text-sm text-slate-500 dark:text-slate-400">Totaal Bespannen</p><p className="text-2xl font-bold text-slate-800 dark:text-white">{sJobs.length}</p></div></div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center"><div className="p-3 bg-orange-50 dark:bg-orange-900/30 rounded-xl mr-4"><Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" /></div><div><p className="text-sm text-slate-500 dark:text-slate-400">Nog te doen</p><p className="text-2xl font-bold text-slate-800 dark:text-white">{sJobs.filter(j => j.status !== 'Klaar').length}</p></div></div>
              
              <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col justify-between">
                  <div className="flex justify-between items-start mb-2">
                      <div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">Nog uit te betalen</p>
                          <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">€ {formatPrice(totalOwed)}</p>
                          <p className="text-xs text-slate-400">{openForPayout.length} rackets open</p>
                      </div>
                      <div className="text-right">
                          <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Vergoeding / Racket</label>
                          <div className="flex items-center justify-end">
                              <span className="text-slate-500 mr-1">€</span>
                              <input 
                                  type="number" 
                                  step="0.5"
                                  className="w-16 p-1 text-right rounded bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                                  defaultValue={rate}
                                  onBlur={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      if (val !== rate) {
                                          onUpdateStringerRate(currentStringer.id, val);
                                      }
                                  }}
                              />
                          </div>
                      </div>
                  </div>
                  {openForPayout.length > 0 && (
                      <button 
                          onClick={() => handleBulkPayStringer(openForPayout)}
                          className="w-full mt-2 py-2 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg text-xs font-bold transition"
                      >
                          Markeer alles als uitbetaald
                      </button>
                  )}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700"><h3 className="font-bold text-slate-800 dark:text-white">Alle opdrachten van {selectedStringerPortal}</h3></div>
              <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                      <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900/50">
                          <tr>
                              <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestStringerSort('date')}>Datum <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                              <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestStringerSort('customer')}>Klant <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                              <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestStringerSort('racket')}>Racket/Snaar/Spanning <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                              <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestStringerSort('status')}>Status <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                              <th className="px-6 py-3">Uitbetaald?</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                          {sJobs.map(job => (
                              <tr key={job.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                  <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">{formatDateEU(job.date)}</td>
                                  <td className="px-6 py-4">
                                      <div 
                                          className="font-medium text-slate-900 dark:text-white cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors inline-block"
                                          onClick={() => { const c = customers.find(cust => cust.id === job.customerId); if (c) openCustomerHistory(c); }}
                                      >
                                          {getCustomerName(job.customerId)}
                                      </div>
                                  </td>
                                  <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
                                      <div className="font-medium text-slate-700 dark:text-slate-300">{getRacketName(job)}</div>
                                      <div>{job.string} {job.isHybrid && job.crossString ? ` / ${job.crossString}` : ''}</div>
                                      <div>{job.tensionMain}/{job.tensionCross} {job.tensionUnit || 'kg'}</div>
                                  </td>
                                  <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${job.status === 'Klaar' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>{job.status}</span></td>
                                  <td className="px-6 py-4">
                                      {job.status === 'Klaar' ? (
                                          <button 
                                              onClick={() => toggleStringerPaid(job)}
                                              className={`px-3 py-1 rounded-full text-xs font-bold transition ${job.stringerPaid ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}
                                          >
                                              {job.stringerPaid ? 'Ja' : 'Nee'}
                                          </button>
                                      ) : (
                                          <span className="text-xs text-slate-400">-</span>
                                      )}
                                  </td>
                                </tr>
                          ))}
                          {sJobs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">Geen opdrachten gekoppeld aan deze bespanner.</td></tr>}
                      </tbody>
                  </table>
              </div>
            </div>
        </div>
    );
}